import React, { useEffect, useRef, useState } from "react";
import { Box, Center } from "@mantine/core";
import { IconPlayerPlayFilled } from "@tabler/icons-react";
import { VideoEvent } from "../../client";
import { DefaultVideoOverlay } from "./DefaultVideoOverlay";
import { thumbnailQueue } from "./thumbnailQueue";

interface VideoThumbnailProps {
  video: VideoEvent;
  compact?: boolean;
  radius?: number;
  inView?: boolean;
}

const thumbnailCache = new Map<string, ImageBitmap>();
const inFlightRequests = new Map<string, Promise<ImageBitmap | null>>();
const failedUrls = new Set<string>();

export const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  video,
  compact = false,
  radius = 20,
  inView = true,
}) => {
  const [hasFrame, setHasFrame] = useState(() => thumbnailCache.has(video.url));
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // If not visible in viewport, do NOT query media or enqueue
    if (!inView) {
      return;
    }

    let active = true;

    // 1. Check in-memory bitmap cache
    if (thumbnailCache.has(video.url)) {
      const bmp = thumbnailCache.get(video.url)!;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = bmp.width;
        canvas.height = bmp.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(bmp, 0, 0);
          setHasFrame(true);
        }
      }
      return;
    }

    // 2. If previously failed or timed out, do not retry
    if (failedUrls.has(video.url)) {
      return;
    }

    // 3. If a request for this URL is already in flight, await its result (deduplication)
    if (inFlightRequests.has(video.url)) {
      inFlightRequests.get(video.url)!.then((bmp) => {
        if (!active || !bmp) return;
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = bmp.width;
          canvas.height = bmp.height;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(bmp, 0, 0);
            setHasFrame(true);
          }
        }
      });
      return;
    }

    // 4. Enqueue a single new request and share its promise
    let resolveInFlight: (bmp: ImageBitmap | null) => void;
    const inFlightPromise = new Promise<ImageBitmap | null>((res) => {
      resolveInFlight = res;
    });
    inFlightRequests.set(video.url, inFlightPromise);

    const cancelQueue = thumbnailQueue.enqueue(
      () =>
        new Promise<void>((resolveQueue) => {
          if (!active) {
            inFlightRequests.delete(video.url);
            resolveInFlight(null);
            resolveQueue();
            return;
          }

          const v = document.createElement("video");
          v.src = `${video.url}#t=0.1`;
          v.preload = "metadata";
          v.muted = true;
          v.playsInline = true;
          v.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");

          let settled = false;
          const finish = (bmp: ImageBitmap | null) => {
            if (settled) return;
            settled = true;
            cleanup();
            inFlightRequests.delete(video.url);
            if (!bmp) {
              failedUrls.add(video.url);
            }
            resolveInFlight(bmp);
            resolveQueue();
          };

          const isFallbackSlate = () => {
            // Fallback slate ("Video Not Available") is 1080p (1920x1080) with duration ~15.015s.
            // Real play-by-play clips are 720p (1280x720) or have play-specific event durations.
            return (
              Math.abs(v.duration - 15.015) < 0.08 &&
              v.videoWidth === 1920 &&
              v.videoHeight === 1080
            );
          };

          const capture = () => {
            if (!active || settled) {
              finish(null);
              return;
            }
            if (isFallbackSlate()) {
              finish(null);
              return;
            }
            const canvas = canvasRef.current;
            if (canvas && v.videoWidth > 0 && v.videoHeight > 0) {
              canvas.width = v.videoWidth;
              canvas.height = v.videoHeight;
              const ctx = canvas.getContext("2d");
              if (ctx) {
                ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
                setHasFrame(true);
                createImageBitmap(canvas)
                  .then((bmp) => {
                    thumbnailCache.set(video.url, bmp);
                    finish(bmp);
                  })
                  .catch(() => finish(null));
                return;
              }
            }
            finish(null);
          };

          const cleanup = () => {
            v.onloadeddata = null;
            v.onseeked = null;
            v.onerror = null;
            v.removeAttribute("src");
            v.load();
          };

          const timeout = setTimeout(() => finish(null), 4000);

          v.onloadeddata = () => {
            clearTimeout(timeout);
            if (v.videoWidth > 0 && v.videoHeight > 0) {
              capture();
            }
          };
          v.onseeked = () => {
            clearTimeout(timeout);
            capture();
          };
          v.onerror = () => {
            clearTimeout(timeout);
            finish(null);
          };
        })
    );

    return () => {
      active = false;
      cancelQueue();
    };
  }, [video.url, inView]);

  return (
    <Box
      style={{
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: radius,
        overflow: "hidden",
        backgroundColor: "#141517",
        position: "relative",
      }}
    >
      {/* 1. Isolated Default Video Overlay Component (Always mounted underneath at zIndex: 0) */}
      <DefaultVideoOverlay video={video} compact={compact} />

      {/* 2. Video Frame Canvas (Fades in over the overlay at zIndex: 1) */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 1,
          opacity: hasFrame ? 1 : 0,
          transition: "opacity 300ms ease-in-out",
          pointerEvents: "none",
        }}
      />

      {/* 3. Frosted Play Button Overlay (Persistent at zIndex: 2) */}
      <Center
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        <Box
          style={{
            width: compact ? 32 : 48,
            height: compact ? 32 : 48,
            borderRadius: "50%",
            backgroundColor: "rgba(0, 0, 0, 0.55)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          }}
        >
          <IconPlayerPlayFilled size={compact ? 16 : 24} />
        </Box>
      </Center>
    </Box>
  );
};
